<template>
  <MainLayout>
    <ContentPageHeader functionName="商品管理" pageName="商品登録">
    </ContentPageHeader>
    
    <div class="product-register-form">
      <div class="form-left">
          <!-- 基本情報セクション -->
          <FormDisplayField 
            label="カテゴリ"
            :value="selectedChildCategory ? selectedChildCategory.category : ''"
          />

          <FormTextField 
            label="商品コード" 
            id="productCode"
            size="medium"
            v-model="product.productCode" 
            :maxlength="10"
          />
          
          <FormSelectSearchField 
            label="メーカー名" 
            id="makerName" 
            v-model="product.makerName"
            :options="makerOptions"
            placeholder="選択してください"
            size="medium"
            ref="makerNameField"
          />
          
          <FormTextField 
            label="商品名" 
            size="medium"
            id="productName" 
            required 
            v-model="product.productName" 
            :enableSuggestions="true"
          />
          
          <FormImageUpload
            label="商品画像"
            id="productImage"
            v-model="product.images"
            :maxImages="10"
            @image-deleted="handleImageDeleted"
            @images-reordered="handleImagesReordered"
            @image-uploaded="handleImageUploaded"
          />
          
          <FormRadioField 
            label="ランク" 
            id="rank" 
            required 
            v-model="product.rank" 
            :options="rankOptions"
            @update:modelValue="onRankChange"
          />
          
          <!-- 付属品 -->
          <FormSelectBoxField
            label="付属品" 
            id="accessory" 
            required 
            v-model="product.accessoryId" 
            :options="accessoryOptions"
            size="small"
            disableSearch="true"
          />
          
          <FormTextareaField 
            label="商品説明" 
            id="productDescription" 
            placeholder="ここに記入してください" 
            v-model="product.productDescription" 
            :rows="4"
            maxlength="1000"
          />
          
          <FormNumberField 
            label="買取価格" 
            id="purchasePrice" 
            required 
            v-model="product.purchasePrice"
            size="small"
            isCurrency
            :isRedLabel="true"
          />

          <FormDualComponent>
            <template #left>
              <FormNumberField 
                label="販売価格（税込）" 
                id="sellingPrice" 
                required 
                v-model="product.sellingPrice"
                size="small"
                isCurrency
              />
            </template>
            <template #right>
              <!-- 税抜価格表示 -->
              <div class="enclosure">
                <div class="A11">税抜販売価格</div>
                <div class="A13">
                  <span class="channel-price">¥ {{ taxExcludedPrice }}</span>
                </div>
              </div>
            </template>
          </FormDualComponent>
          
          <!-- 価格調整 -->
          <FormSelectSearchField 
            label="価格調整" 
            id="priceAdjustment" 
            v-model="priceAdjustment"
            :options="priceAdjustmentOptions"
            placeholder="選択してください"
            size="small"
            ref="priceAdjustmentField"
          />

          <!-- 販売チャンネル一覧 -->
          <FormCollapsibleSection title="販売チャンネル別価格" :required="false" :headerJustify="'left'">
            <div v-for="(channel) in salesChannels" :key="channel.id" class="sales-channel-item">
              <div class="enclosure">
                <div class="A11">{{ channel.acc }}</div>
                <div class="A13">
                  <span class="channel-price">
                    ¥ {{ formatPrice(calculateChannelPrice(channel.id)) }}
                    <template v-if="getChannelPriceFromResponse(channel.id) !== null">
                      ／&nbsp;現在の販売価格&nbsp;&nbsp;¥{{ formatPrice(getChannelPriceFromResponse(channel.id)) }}
                    </template>
                  </span>
                  <span v-if="!hasPriceIncreaseInfo(channel.id)" class="price-warning">
                    値上げ情報及び送料が見つかりませんでした
                  </span>
                </div>
              </div>
            </div>
          </FormCollapsibleSection>

          <FormNumberField 
            label="在庫数" 
            id="stockQuantity" 
            size="small"
            v-model="product.stockQuantity" 
            disabled
          />
          
          <FormSelectBoxField 
            label="買取店舗" 
            id="purchaseStore" 
            v-model="product.purchaseStore"
            :options="storeOptions"
            placeholder="選択してください"
            required
            size="small"
          />

          <FormSelectBoxField 
            label="現在店舗" 
            id="currentStore" 
            v-model="product.currentStore"
            :options="storeOptions"
            placeholder="選択してください"
            required
            @update:modelValue="handleStoreSelection"
            @blur="handleStoreSelection"
            size="small"
          />

          <FormDateField 
            label="買取日"
            id="purchaseDate" 
            v-model="product.purchaseDate"
            required
          />
          
          <FormDualComponent>
            <template #left>
              <FormDateField 
                label="販売日"
                id="saleDate" 
                v-model="product.saleDate"
              />
            </template>
            <template #right>
              <div v-if="isPublicStatusSold" class="enclosure">
                <div class="A11">販売チャンネル</div>
                <div class="A13">
                  <span class="channel-price">{{ getSalesChannelName(product.salesChannelId) }}</span>
                </div>
              </div>
            </template>
          </FormDualComponent>
          
          <FormDualComponent>
            <template #left>
              <FormRadioField 
                label="ラベル"
                required
                size="small"
                id="labelSize" 
                v-model="product.labelSize" 
                :options="[
                  { value: '1', label: '小' },
                  { value: '2', label: '大' }
                ]"
                :disabled="selectedChildCategory !== null"
              />
            </template>
            <template #right>
              <button class="btn btn-secondary" @click="printLabel">ラベル印刷</button>
            </template>
          </FormDualComponent>
          
          <!-- 価格変更履歴セクション（商品IDがある場合のみ表示） -->
          <FormCollapsibleSection v-if="product.id" title="価格変更履歴" :required="false" :initialCollapsed="true">
            <div v-if="priceLogs.length > 0" class="price-history-table">
              <table class="table">
                <thead>
                  <tr>
                    <th>変更日</th>
                    <th>変更前価格（税込）</th>
                    <th>変更後価格（税込）</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(log, index) in priceLogs" :key="index">
                    <td>{{ formatDate(log.changeDate) }}</td>
                    <td>¥ {{ formatPrice(log.priceBeforeChangeTaxIncluded) }}</td>
                    <td>¥ {{ formatPrice(log.priceAfterChangeTaxIncluded) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else class="no-price-history">
              価格変更履歴はありません
            </div>
          </FormCollapsibleSection>
      </div>
      
      <div class="form-right">
        <!-- カテゴリセクション -->
        <FormCollapsibleSection title="カテゴリ" :required="true">
          <FormSearchField 
            label="" 
            id="categorySearch" 
            v-model="categorySearchQuery" 
            placeholder="検索..."
            @search="searchCategories"
            @input="searchCategories"
          />
          <FormHierarchicalRadioList
            id="categoryList"
            v-model="product.category"
            :categories="categories"
            @update:modelValue="handleCategorySelection"
          />
        </FormCollapsibleSection>
        
        <!-- 商品ステータスセクション（タグ） -->
        <FormCollapsibleSection title="商品ステータス" :required="false">
          <FormToggleButton
            v-model="product.selectedTags"
            :options="productStatusOptions"
          />
        </FormCollapsibleSection>
        
        <!-- 登録日・更新日セクション -->
        <FormCollapsibleSection title="登録日・更新日" :required="false">
          <FormInfoRow
            icon="fas fa-flag"
            label="登録日"
            :value="product.registrationDate"
          />
          <FormInfoRow
            icon="fas fa-sync-alt"
            label="更新日"
            :value="product.updateDate"
          />
          <FormInfoRow
            icon="fas fa-user"
            label="最終更新者"
            :value="product.lastUpdatedBy"
          />
        </FormCollapsibleSection>
        
        <!-- ショップ用メモ欄セクション -->
        <FormCollapsibleSection title="ショップ用メモ欄" :required="false">
          <FormTextareaField 
            label="" 
            id="shopMemo" 
            placeholder="ショップ用メモを入力してください" 
            v-model="product.shopMemo" 
            :rows="6"
            :showLabel="false" 
            :showRequired="false"
            maxlength="1000"
          />
        </FormCollapsibleSection>
      </div>
    </div>

    <template #footer-buttons>
      <div v-if="isPublicStatusSold" class="footer-publication-status">
        <SimpleDropdown 
          id="footerSalesChannel" 
          v-model="selectedSalesChannelId"
          :options="salesChannelOptions"
          placeholder="選択してください"
        />
      </div>
      <div class="footer-publication-status">
        <SimpleDropdown 
          id="footerPublicationStatus" 
          v-model="product.publicationStatus"
          :options="publicStateOptions"
          placeholder="選択してください"
        />
      </div>
      <button class="btn btn-primary" @click="submitForm">登録</button>
    </template>
  </MainLayout>
</template>

<script>
import MainLayout from '@/components/layout/MainLayout.vue'
import ContentPageHeader from '@/components/layout/ContentPageHeader.vue'
import FormDisplayField from '@/components/form/FormDisplayField.vue'
import FormTextField from '@/components/form/FormTextField.vue'
import FormSelectSearchField from '@/components/form/FormSelectSearchField.vue'
import FormSelectBoxField from '@/components/form/FormSelectBoxField.vue'
import FormRadioField from '@/components/form/FormRadioField.vue'
import FormTextareaField from '@/components/form/FormTextareaField.vue'
import FormDateField from '@/components/form/FormDateField.vue'
import FormNumberField from '@/components/form/FormNumberField.vue'
import FormCollapsibleSection from '@/components/form/FormCollapsibleSection.vue'
import FormSearchField from '@/components/form/FormSearchField.vue'
import FormHierarchicalRadioList from '@/components/form/FormHierarchicalRadioList.vue'
import FormToggleButton from '@/components/form/FormToggleButton.vue'
import FormImageUpload from '@/components/form/FormImageUpload.vue'
import FormInfoRow from '@/components/form/FormInfoRow.vue'
import SimpleDropdown from '@/components/form/SimpleDropdown.vue'
import FormDualComponent from '@/components/form/FormDualComponent.vue'
import service from '../../services/service';
import printAgentService from '../../services/printAgentService';
import constants from '../../constants/constants.json';
import { showLoading, hideLoading } from '@/utils/loadingEventBus';

export default {
  name: 'ProductRegisterView',
  components: {
    MainLayout,
    ContentPageHeader,
    FormDisplayField,
    FormTextField,
    FormSelectSearchField,
    FormSelectBoxField,
    FormRadioField,
    FormTextareaField,
    FormDateField,
    FormNumberField,
    FormCollapsibleSection,
    FormSearchField,
    FormHierarchicalRadioList,
    FormToggleButton,
    FormImageUpload,
    FormInfoRow,
    SimpleDropdown,
    FormDualComponent
  },
  data() {
    return {
      categorySearchQuery: '',
      categories: [],  // 整形後のカテゴリデータ
      originalCategories: [], // APIから取得した元のカテゴリデータ
      makerOptions: [], // メーカー一覧
      publicStateOptions: [], // 公開状態一覧
      accountOptions: [], // アカウント一覧
      storeOptions: [], // 店舗一覧
      storeData: [], // 店舗データ（タグIDを含む）
      productStatusOptions: [], // 商品ステータス一覧
      taxRate: 1.1, // 消費税率（10%）
      rankOptions: [], // ランク一覧
      statusOptions: [], // ステータス一覧
      selectedChildCategory: null,
      priceLogs: [], // 価格変更履歴
      priceAdjustment: '',
      priceAdjustmentOptions: [
        { value: '-5', label: '-5' },
        { value: '-10', label: '-10' },
        { value: '-15', label: '-15' },
        { value: '-20', label: '-20' },
        { value: '-30', label: '-30' },
        { value: '-50', label: '-50' }
      ],
      priceByChannel: [],
      salesChannels: [],
      salesChannelOptions: [],
      selectedSalesChannelId: "1",
      accessoryOptions: [], // 付属品一覧
      product: {
        id: null, // 商品ID（編集時に使用）
        productCode: '',
        makerName: '',
        productName: '',
        rank: '',
        productDescription: '',
        purchasePrice: null,
        sellingPrice: null,
        sellingPriceExTax: null,
        priceAdjustment1: {
          type: '',
          priceIncTax: null,
          priceExTax: null
        },
        priceAdjustment2: {
          type: '',
          priceIncTax: null,
          priceExTax: null
        },
        stockQuantity: 1,
        purchaseStore: '',
        currentStore: '',
        purchaseDate: this.getTodayDate(), // 本日日付をデフォルト値に設定
        saleDate: '',
        publicationStatus: '',
        labelSize: '1', // デフォルトは小
        category: '',
        selectedTags: [],
        images: [],
        existingImageIds: [], // 既存画像のID一覧
        deletedImageIds: [], // 削除された既存画像のID一覧
        registrationDate: '',
        updateDate: '',
        lastUpdatedBy: '',
        shopMemo: '',
        accessoryId: null, // 付属品ID
        isChanged: false, // 画像データが変更されたかどうか
        salesChannelId: null, // 販売チャンネルID
      },
      isEditDescriptionInitForOrc: false, // 商品説明編集時初期化中フラグ
      isEditDescriptionInitForHcs: false // 商品説明編集時初期化中フラグ（HCS用）
    }
  },
  computed: {
    // 税抜価格の計算
    taxExcludedPrice() {
      if (!this.product.sellingPrice) return 0;
      // 税込価格から税抜価格を計算（小数点以下切り捨て）
      return Math.floor(this.product.sellingPrice / this.taxRate);
    },
    // 価格調整割合の計算
    priceAdjustmentRatio() {
      if (!this.priceAdjustment) return 1;
      // 価格調整値をパーセント表記から小数に変換（例：-5 → 0.95）
      return 1 + (parseInt(this.priceAdjustment) / 100);
    },
    // ユーザーの権限
    userAuthority() {
      return this.$store.getters['auth/currentUser']?.authority || 0;
    },
    // ユーザーの所属店舗ID
    userStoreId() {
      return this.$store.getters['auth/currentUser']?.storeId || 0;
    },
    
    // 公開ステータスが「販売済」かどうかを判定
    isPublicStatusSold() {
      return this.product.publicationStatus === constants.ProductStatus.SOLD.id.toString();
    }
  },
  watch: {
    // 公開ステータスの変更を監視
    'product.publicationStatus': function(newValue, oldValue) {
      // 初期ロード時や編集時の既存データ表示時は処理しない
      if (!oldValue || oldValue === newValue) return;
      
      if (newValue === constants.ProductStatus.SOLD.id.toString()) {
        // 販売済に変更された場合、販売日に現在日付を設定
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        this.product.saleDate = `${year}-${month}-${day}`;
      } else {
        // 販売済以外に変更された場合、販売日をクリア
        this.product.saleDate = '';
      }
    }
  },
  async mounted() {
    // 初期データ取得
    await this.getInitData();
    
    // URLパラメータからIDを取得し、存在する場合は商品情報を取得
    const productId = this.$route.params.id;
    if (productId) {
      this.fetchProductById(productId);
    } else {
      // 公開ステータスのデフォルト設定
      this.setDefaultProductStatus();
    }
  },
  methods: {
    // 本日日付を取得する（YYYY-MM-DD形式）
    getTodayDate() {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },

    // 初期データ取得処理
    async getInitData() {
      showLoading();
      try {
        // カテゴリ一覧を取得するAPI呼び出し
        const categoryResponse = await service.getCategoryList();
        this.originalCategories = categoryResponse.data.categories;
        this.formatCategoriesForDisplay();

        // メーカー一覧を取得するAPI呼び出し
        const makerResponse = await service.getMakerList();
        this.makerOptions = makerResponse.data.makers.map(maker => {
          return { value: maker.id.toString(), label: maker.maker };
        });

        // 公開状態一覧を取得するAPI呼び出し
        const publicStateResponse = await service.getPublicStateList();
        this.publicStateOptions = publicStateResponse.data.publicStates.map(state => {
          return { value: state.id.toString(), label: state.publicState };
        });

        // アカウント一覧を取得するAPI呼び出し
        const AccountResponse = await service.getAccountList();
        this.accountOptions = AccountResponse.data.accounts.map(account => {
          return { value: account.id.toString(), label: account.accountName };
        });

        // 商品ステータス一覧を取得するAPI呼び出し
        const tagResponse = await service.getTagList();
        this.productStatusOptions = tagResponse.data.tags.map(tag => {
          return { label: tag.tagName, value: tag.id.toString() };
        });

        // 店舗一覧を取得するAPI呼び出し
        const storeResponse = await service.getStoreList();
        // 店舗データをタグIDと一緒に保存
        this.storeData = storeResponse.data.stores;
        
        // 付属品一覧を取得するAPI呼び出し
        try {
          const accessoryResponse = await service.getAccessoryList();
          this.accessoryOptions = accessoryResponse.data.accessories.map(accessory => {
            return { value: accessory.id.toString(), label: accessory.accessory };
          });
          
          // 先頭の付属品を選択
          const productId = this.$route.params.id;
          if (!productId && 0 < this.accessoryOptions.length) {
            this.product.accessoryId = this.accessoryOptions[0].value;
          }
        } catch (error) {
          console.error('付属品一覧の取得に失敗しました:', error);
          alert('付属品一覧の取得中にエラーが発生しました。');
          this.accessoryOptions = [];
        }
        this.storeOptions = storeResponse.data.stores.map(store => {
          return { value: store.id.toString(), label: store.storeName };
        });
        // ログインユーザーの店舗IDを取得
        const userStoreId = this.$store.getters['auth/currentUser']?.storeId;
        if (userStoreId) {
          // 買取店舗と現在店舗のデフォルト値を設定
          this.product.purchaseStore = userStoreId.toString();
          this.product.currentStore = userStoreId.toString();
          // 現在店舗の選択に基づいてタグを設定
          this.handleStoreSelection(userStoreId.toString());
        }

        // 販売チャンネル一覧を取得するAPI呼び出し
        const salesChannelResponse = await service.getSalesChannelList();
        this.salesChannels = salesChannelResponse.data.salesChannels;
        this.salesChannelOptions = salesChannelResponse.data.salesChannels.map(channel => {
          return { value: channel.id.toString(), label: channel.acc };
        });

      } catch (error) {
        console.error('初期情報の取得に失敗しました:', error);
        alert('初期情報の取得中にエラーが発生しました。');
      } finally {
        hideLoading();
      }
    },

    // 公開ステータスを未登録に設定
    setDefaultProductStatus() {
      const unregisteredId = constants.ProductStatus.UNREGISTERED.id.toString();
      const found = this.salesChannelOptions.find(opt => opt.value === unregisteredId);
      if (found) {
        this.product.publicationStatus = unregisteredId;
      }
    },

    // APIから取得したカテゴリデータを表示用に整形
    formatCategoriesForDisplay() {
      this.categories = this.originalCategories.map(parent => {
        return {
          title: parent.categoryType,
          items: parent.childCategories.map(child => {
            return {
              label: child.category,
              value: child.id.toString(),
              data: child // 子カテゴリの全データを保持
            };
          })
        };
      });
      
      // 新規登録時（product_idが存在しない場合）に最初のカテゴリを選択
      if (0 < this.$route.params.id || this.categories.length < 1 || this.categories[0].items.length < 1) return;
      const firstCategory = this.categories[0].items[0];
      this.product.category = firstCategory.value;
      // カテゴリ選択時の処理を呼び出してランクとラベルを設定
      this.handleCategorySelection(firstCategory.value);
    },
    
    // 現在店舗選択時の処理
    handleStoreSelection(storeId) {
      if (!this.storeData) return;
      
      // 選択された店舗のタグIDを取得
      const selectedStore = this.storeData.find(store => store.id.toString() === storeId);
      if (!selectedStore || !selectedStore.tagMasterId) {
        // 店舗にタグIDがない場合は選択されているタグを解除する
        this.product.selectedTags = [];
        return;
      }
      // 店舗のタグIDに一致するタグを選択状態にする
      this.product.selectedTags = [selectedStore.tagMasterId.toString()];
    },
   
    // 販売チャンネルの価格を計算する
    calculateChannelPrice(channelId) {
      if (!this.product.sellingPrice || !this.selectedChildCategory) return 0;
      
      // 販売価格（税込）
      const sellingPrice = parseInt(this.product.sellingPrice);
      
      // 価格調整割合
      const adjustmentRatio = this.priceAdjustmentRatio;
      
      // 基準金額 = 販売価格（税込）* 価格調整割合
      const baseAmount = Math.ceil(sellingPrice * adjustmentRatio);
      
      // カテゴリの値上げ率と送料を取得（基準金額に基づいて）
      const { increaseRate, shippingFee } = this.getPriceIncreaseInfo(channelId, baseAmount);
      
      // 価格計算: 基準金額 * カテゴリの値上げ率 + 送料
      const rawAmount = (baseAmount * increaseRate) + shippingFee;
      return Math.ceil(rawAmount / constants.ROUNDING_FACTOR) * constants.ROUNDING_FACTOR;
    },
    
    // カテゴリの値上げ率と送料を取得する
    getPriceIncreaseInfo(channelId, baseAmount) {
      // デフォルト値
      const defaultInfo = { increaseRate: 1.0, shippingFee: 0 };
      
      // カテゴリが選択されていない場合はデフォルト値を返す
      if (!this.selectedChildCategory) return defaultInfo;
      
      // 選択されたカテゴリの値上げ情報を検索
      const priceIncreases = this.selectedChildCategory.priceIncreases || [];
      const priceIncrease = priceIncreases.find(pi => pi.salesChannel === channelId);
      
      // 値上げ情報が見つからない場合はデフォルト値を返す
      if (!priceIncrease || !priceIncrease.details || priceIncrease.details.length === 0) {
        return defaultInfo;
      }
      
      // 価格範囲に該当する値上げ詳細を検索
      const detail = priceIncrease.details.find(d => {
        const min = d.sellingPriceMin || 0;
        const max = d.sellingPriceMax || Number.MAX_SAFE_INTEGER;
        return min <= baseAmount && baseAmount <= max;
      });
      
      // 値上げ詳細が見つからない場合はデフォルト値を返す
      if (!detail) return defaultInfo;
      
      // 値上げ率をパーセントから小数に変換（例：10 → 1.10）
      const increaseRate = 1 + (detail.priceIncreaseRate / 100);
      
      return {
        increaseRate: increaseRate,
        shippingFee: detail.shippingFee || 0
      };
    },
    
    // 値上げ情報があるかどうかを確認する
    hasPriceIncreaseInfo(channelId) {
      if (!this.selectedChildCategory || !this.selectedChildCategory.priceIncreases) return false;
      
      const priceIncrease = this.selectedChildCategory.priceIncreases.find(pi => pi.salesChannel === channelId);
      return priceIncrease && priceIncrease.details && 0 < priceIncrease.details.length;
    },
    
    // 商品IDによる商品情報を取得するAPI呼び出し
    async fetchProductById(id) {
      try {
        showLoading();
        const response = await service.getProductById(id);
        if (response.data.success) {
          const productData = response.data;
          
          // 商品IDを保存
          this.product.id = productData.id;
          
          // 商品情報をフォームに設定
          this.product.productCode = productData.itemCode || '';
          this.product.makerName = productData.makerId ? productData.makerId.toString() : '';
          this.product.productName = productData.productName || '';
          this.product.rank = productData.rankId ? productData.rankId.toString() : '';
          this.product.productDescription = productData.productDescription || '';
          this.product.purchasePrice = productData.purchasePrice || 0;
          this.product.sellingPrice = productData.currentSellingPriceTaxIncluded || 0;
          this.product.stockQuantity = productData.stockQuantity || 0;
          this.product.purchaseStore = productData.purchaseStore ? productData.purchaseStore.toString() : '';
          this.product.currentStore = productData.currentStore ? productData.currentStore.toString() : '';
          this.product.purchaseDate = this.formatDateForInput(productData.purchaseDate) || '';
          this.product.saleDate = this.formatDateForInput(productData.salesDate) || '';
          this.product.publicationStatus = productData.publicStateId ? productData.publicStateId.toString() : '';
          this.product.category = productData.categoryId ? productData.categoryId.toString() : '';
          this.product.shopMemo = productData.shopMemoField || '';
          this.product.accessoryId = productData.accessoryId ? 
            productData.accessoryId.toString() : (0 < this.accessoryOptions.length ? this.accessoryOptions[0].value : '');
          this.product.salesChannelId = productData.salesChannelId ? productData.salesChannelId.toString() : constants.SalesChannel.STORE.id.toString();
          this.selectedSalesChannelId = productData.salesChannelId ? productData.salesChannelId.toString() : constants.SalesChannel.STORE.id.toString();
          this.priceByChannel = response.data.priceByChannel || [];

          // 登録日、更新日、最終更新者を設定
          this.product.registrationDate = productData.createdAt || '';
          this.product.updateDate = productData.updatedAt || '';
          this.product.lastUpdatedBy = productData.updatedBy || '';
          
          // 画像データを設定
          if (productData.images && productData.images.length > 0) {
            this.loadProductImages(productData.images);
          } else {
            // 画像がない場合は空の配列を設定
            this.product.images = [];
            this.product.existingImageIds = [];
            this.product.deletedImageIds = [];
          }
          
          // カテゴリが選択されている場合、対応するランクを設定
          if (this.product.category) {
            this.updateRankOptions(productData.rankId);
          }
          
          // 価格変更履歴を設定
          this.priceLogs = productData.priceLogs || [];

          // 商品ステータスを設定
          if (Array.isArray(productData.productTags) && Array.isArray(this.productStatusOptions)) {
            this.product.selectedTags = this.productStatusOptions
              .filter(opt => productData.productTags.includes(Number(opt.value)))
              .map(opt => opt.value);
          }
        } else {
          alert('商品情報の取得に失敗しました: ' + response.data.message);
        }
      } catch (error) {
        alert('商品情報の取得中にエラーが発生しました');
      } finally {
        hideLoading();
      }
    },
    
    // 価格変更履歴関連のメソッドは商品詳細取得時に一緒に取得するように変更したため削除
    
    // 日付フォーマット（YYYY/MM/DD形式）
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}/${month}/${day}`;
    },
    
    // 日付フォーマット（YYYY-MM-DD形式 - input[type="date"]用）
    formatDateForInput(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    
    // 価格フォーマット（カンマ区切り）
    formatPrice(price) {
      if (price === null || price === undefined) return '0';
      return price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    },
    
    // 商品画像データをロードする
    async loadProductImages(images) {
      this.product.images = [];
      this.product.existingImageIds = []; // 既存画像のIDを保存
      this.product.deletedImageIds = []; // 削除された画像のIDをリセット
      this.product.isChanged = false; // 初期状態では変更なし
      
      // 画像をsortOrderの昇順でソート
      const sortedImages = [...images].sort((a, b) => a.sortOrder - b.sortOrder);
      
      for (const image of sortedImages) {
        try {
          // 画像URLを構築
          const imageUrl = image.imageUrl || `/images/products_${this.product.id}/${image.fileName}`;
          
          // 既存画像情報をオブジェクトとして保存
          const imageObj = {
            id: image.id,
            productId: image.productId,
            fileName: image.fileName,
            sortOrder: image.sortOrder,
            preview: imageUrl,
            isExisting: true
          };
          
          this.product.images.push(imageObj);
          this.product.existingImageIds.push(image.id);
        } catch (error) {
          console.error('画像のロードに失敗しました:', error);
        }
      }
    },
    
    // 画像削除時のハンドラ
    handleImageDeleted(imageId) {
      // 削除された画像IDを記録
      if (!this.product.deletedImageIds.includes(imageId)) {
        this.product.deletedImageIds.push(imageId);
      }
      
      // 既存画像IDリストから削除
      const index = this.product.existingImageIds.indexOf(imageId);
      if (index !== -1) {
        this.product.existingImageIds.splice(index, 1);
      }
      
      // 変更フラグを設定
      this.product.isChanged = true;
    },
    
    // 画像並び替え時のハンドラ
    handleImagesReordered(images) {
      // 画像の並び順が変更された場合、sortOrderを更新
      this.product.images = images;
      // 変更フラグを設定
      this.product.isChanged = true;
    },

    // 画像アップロード時のハンドラ
    handleImageUploaded() {
      // 変更フラグを設定
      this.product.isChanged = true;
    },

    // カテゴリ選択時の処理
    handleCategorySelection(categoryId) {
      // 選択されたカテゴリIDから子カテゴリを検索
      for (const parent of this.originalCategories) {
        for (const child of parent.childCategories) {
          if (child.id.toString() === categoryId) {
            this.selectedChildCategory = child;
            
            // ランクの設定 - 文字列に変換して一貫性を保つ
            this.rankOptions = child.ranks.map(rank => {
              return { value: rank.id.toString(), label: rank.rank };
            });
            
            // ラベルサイズの設定
            this.product.labelSize = child.isLargeLabel === 1 ? '2' : '1';
            break;
          }
        }
      }
      // カテゴリ変更時にランクをリセットし、1つ目があれば自動選択
      this.product.rank = 0 < this.rankOptions.length ? this.rankOptions[0].value : '';
      // 編集時の初期表示時のみ何もしない
      const productId = this.$route.params.id;
      if (!this.isEditDescriptionInitForHcs && productId) {
        this.isEditDescriptionInitForHcs = true;
        return;
      }
      // カテゴリIDとランクIDでテンプレートを更新
      this.updateProductDescriptionTemplate();
    },

    // ランク変更時の処理
    onRankChange() {
      // 編集の初期表示時のみ何もしない
      const productId = this.$route.params.id;
      if (!this.isEditDescriptionInitForOrc && productId) {
        this.isEditDescriptionInitForOrc = true;
        return;
      }
      // カテゴリIDとランクIDでテンプレートを更新
      this.updateProductDescriptionTemplate();
    },
    
    // 商品編集時のランクオプション更新処理
    updateRankOptions(rankId) {
      const categoryId = this.product.category;
      if (!categoryId || !this.originalCategories) return;
      
      // 選択されたカテゴリIDから子カテゴリを検索
      let foundCategory = false;
      let categoryIndex = -1;
      let itemIndex = -1;
      let rankIdToSelect = null;
      
      for (let i = 0; i < this.originalCategories.length; i++) {
        const parent = this.originalCategories[i];
        for (let j = 0; j < parent.childCategories.length; j++) {
          const child = parent.childCategories[j];
          // 文字列に変換して比較
          if (child.id.toString() === categoryId.toString()) {
            this.selectedChildCategory = child;
            foundCategory = true;
            categoryIndex = i;
            itemIndex = j;
            
            // ランクの設定
            this.rankOptions = child.ranks.map(rank => {
              return { value: rank.id.toString(), label: rank.rank };
            });
            
            if (rankId) {
              rankIdToSelect = rankId.toString();
              this.product.rank = rankIdToSelect;
            }
            
            // ラベルサイズの設定
            this.product.labelSize = child.isLargeLabel === 1 ? '2' : '1';
            break;
          }
        }
        if (foundCategory) break;
      }
      
      if (foundCategory) {
        // DOMが更新された後にラジオボタンを直接操作
        this.$nextTick(() => {
          // カテゴリラジオボタンのIDを構築
          const categoryRadioId = `categoryList-${categoryIndex}-${itemIndex}`;
          
          // カテゴリラジオボタンを探して選択状態にする
          const categoryRadioButton = document.getElementById(categoryRadioId);
          if (categoryRadioButton) {
            categoryRadioButton.checked = true;
            // イベントを発火させてVueに変更を通知
            categoryRadioButton.dispatchEvent(new Event('change', { bubbles: true }));
          } else {
            console.error("Category radio button not found with ID:", categoryRadioId);
          }
          
          // ランクラジオボタンの処理
          if (rankIdToSelect) {
            // ランクラジオボタンのIDを構築
            const rankRadioId = `rank-${rankIdToSelect}`;
            
            // ランクラジオボタンを探して選択状態にする
            this.$nextTick(() => {
              const rankRadioButton = document.getElementById(rankRadioId);
              if (rankRadioButton) {
                rankRadioButton.checked = true;
                // イベントを発火させてVueに変更を通知
                rankRadioButton.dispatchEvent(new Event('change', { bubbles: true }));
              } else {
                console.error("Rank radio button not found with ID:", rankRadioId);
              }
            });
          }
        });
      }
    },

    // カテゴリ検索 (入力内容の部分一致で検索)
    searchCategories() {
      // 現在選択されているカテゴリIDとランクを保存
      const selectedCategoryId = this.product.category;
      const selectedRank = this.product.rank;
      const currentRankOptions = [...this.rankOptions];
      const currentSelectedChildCategory = this.selectedChildCategory;
      
      if (!this.categorySearchQuery) {
        // 検索クリア時は元のカテゴリリストを表示するが、選択状態は維持する
        const preserveSelection = !!selectedCategoryId;
        
        // カテゴリリストを元に戻す（選択状態をリセットしないように修正）
        this.categories = this.originalCategories.map(parent => {
          return {
            title: parent.categoryType,
            items: parent.childCategories.map(child => {
              return {
                label: child.category,
                value: child.id.toString(),
                data: child
              };
            })
          };
        });
        
        // 選択状態を維持
        if (preserveSelection) {
          this.product.category = selectedCategoryId;
          this.product.rank = selectedRank;
          this.rankOptions = currentRankOptions;
          this.selectedChildCategory = currentSelectedChildCategory;
        }
        return;
      }
      
      const query = this.categorySearchQuery.toLowerCase();
      this.categories = this.originalCategories
        .map(parent => {
          const filteredItems = parent.childCategories
            .filter(child => child.category.toLowerCase().includes(query))
            .map(child => {
              return {
                label: child.category,
                value: child.id.toString(),
                data: child
              };
            });
            
          return {
            title: parent.categoryType,
            items: filteredItems
          };
        })
        .filter(category => category.items.length > 0);
    },

    // 商品登録API呼び出し
    async submitForm() {
      // バリデーションチェック
      const errorMessage = this.validationCheck();
      if (errorMessage != null) {
        alert(errorMessage);
        return;
      }

      // 確認ダイアログを表示（編集時と登録時でメッセージを変える）
      const action = this.product.id ? '更新' : '登録';
      const isConfirmed = confirm(`商品を${action}します。\nよろしいですか。`);
      if (!isConfirmed) {
        return; // キャンセルされた場合は処理を中止
      }
      
      try {
        showLoading();
        const action = this.product.id ? 'update' : 'register';
        await this.handleProductSubmission(action);
      } catch (error) {
        alert('商品の登録中にエラーが発生しました。');
      } finally {
        hideLoading();
      }
    },
    
    // ラベル印刷
    async printLabel() {
      // ラベル印刷用のバリデーションチェック
      const errorMessage = this.validateLabelPrint();
      if (errorMessage) {
        alert(errorMessage);
        return;
      }

      try {
        showLoading();
        
        // 印刷処理の確認
        const isConfirmed = confirm('商品ラベルを印刷します。よろしいですか？');
        if (!isConfirmed) {
          return;
        }
        
        // ラベルサイズを取得
        const labelSize = this.product.labelSize === '2' ? constants.LabelSize.LARGE.id : constants.LabelSize.SMALL.id;
        // フォームデータの作成
        const formData = {
          labelSize: labelSize,
          productId: this.product.id,
          productCode: this.product.productCode,
          productName: this.product.productName,
          // 自動値下げがあるので価格はここで設定しない
        };
        
        // 62mmラベルの場合は追加情報を設定
        if (labelSize === constants.LabelSize.LARGE.id) {
          // メーカー名の取得（IDから名前に変換）
          const makerOption = this.makerOptions.find(option => option.value === this.product.makerName);
          formData.makerName = makerOption ? makerOption.label : '';
          
          // ランク名の取得（IDから名前に変換）
          const rankOption = this.rankOptions.find(option => option.value === this.product.rank);
          formData.rank = rankOption ? rankOption.label : '';
          
          // 付属品情報（accessoryIdから取得、なければ「なし」）
          formData.accessory = this.product.accessoryId ? this.getAccessoryName(this.product.accessoryId) : 'なし';
        }
        // ラベル生成APIを呼び出し（フォームデータを使用）
        const response = await service.generateLabelFromForm(formData);
        
        if (!response.data.success) {
          alert('ラベル生成に失敗しました。');
          return;
        }
        // 商品コードの数字以外の文字を除去
        const sanitizedProductCode = this.product.productCode.replace(/[^0-9]+/g, '');
        // ジョブ名の生成（商品コードがない場合は「0000000000」を使用）
        const jobName = `Label-${sanitizedProductCode ? sanitizedProductCode : '0000000000'}`;

        // 印刷エージェントサービスを使用して印刷
        const success = await printAgentService.printLabel(
          response.data.pdfBase64,
          labelSize,
          jobName
        );
        
        if (success) {
          alert('ラベル印刷を開始しました');
        }
      } catch (error) {
        console.error('ラベル印刷エラー:', error);
        alert('ラベル印刷処理中にエラーが発生しました。');
      } finally {
        hideLoading();
      }
    },
    
    // ラベル印刷用のバリデーションチェック
    validateLabelPrint() {
      if (!this.product.productCode) {
        return '商品コードは必須です';
      }
      if (!this.product.productName) {
        return '商品名は必須です';
      }
      if (this.product.labelSize === '2' && !this.product.rank) {
        return 'ランクは必須です';
      }
      if (!this.product.sellingPrice) {
        return '販売価格（税込）は必須です';
      }
      // 桁数チェック
      if (32 < this.product.productCode.length) {
        return '商品コードは32文字以内で入力してください';
      }
      if (64 < this.product.productName.length) {
        return '商品名は64文字以内で入力してください';
      }
      // 権限チェック
      if (this.userAuthority === constants.Authority.MANAGER.id || this.userAuthority === constants.Authority.STAFF.id) {
        // 現在店舗が自店舗かチェック
        if (parseInt(this.product.currentStore) !== this.userStoreId) {
          return '自店舗の商品のみラベル印刷できます。現在店舗に他店舗が選択されています。';
        }
      }

      return null;
    },
    
    // 付属品IDから付属品名を取得
    getAccessoryName(accessoryId) {
      if (!accessoryId) return 'なし';
      const accessory = this.accessoryOptions.find(option => option.value === accessoryId.toString());
      return accessory ? accessory.label : 'なし';
    },
    
    // 公開処理
    async publishProduct() {
      // バリデーションチェック
      const errorMessage = this.validationCheck();
      if (errorMessage != null) {
        alert(errorMessage);
        return;
      }
      
      // 確認ダイアログを表示
      const isConfirmed = confirm('商品を公開します。\nよろしいですか。');
      if (!isConfirmed) {
        return; // キャンセルされた場合は処理を中止
      }
      
      try {
        showLoading();
        await this.handleProductSubmission('public');
      } catch (error) {
        console.error('商品公開エラー:', error);
        alert('商品の公開中にエラーが発生しました。');
      } finally {
        hideLoading();
      }
    },

    async handleProductSubmission(action) {
      // 商品登録リクエストの作成
      const imageDataList = await this.processImageDataList(this.product.images);
      
      // 価格調整を適用した販売価格を計算
      let adjustedSellingPrice = this.product.sellingPrice ? parseInt(this.product.sellingPrice) : 0;
      
      // 価格調整が選択されている場合、その割合を適用
      if (this.priceAdjustment) {
        const adjustmentRatio = 1 + (parseInt(this.priceAdjustment) / 100);
        adjustedSellingPrice = Math.floor(adjustedSellingPrice * adjustmentRatio);
      }
      
      const productData = {
        mode: action, // 登録モード（新規登録、更新、公開）
        id: this.product.id, // 商品ID（編集時のみ存在）
        itemCode: this.product.productCode,
        makerId: this.product.makerName ? parseInt(this.product.makerName) : null,
        productName: this.product.productName,
        categoryId: this.product.category ? parseInt(this.product.category) : null,
        rankId: this.product.rank ? parseInt(this.product.rank) : null,
        accessoryId: this.product.accessoryId && this.product.accessoryId !== '' ? parseInt(this.product.accessoryId) : null,
        productDescription: this.product.productDescription,
        purchasePrice: this.product.purchasePrice ? parseInt(this.product.purchasePrice) : 0,
        sellingPriceTaxIncluded: adjustedSellingPrice,
        sellingPriceTaxExcluded: adjustedSellingPrice ? Math.floor(adjustedSellingPrice / this.taxRate) : 0,
        stockQuantity: this.product.stockQuantity ? parseInt(this.product.stockQuantity) : 0,
        purchaseStore: this.product.purchaseStore ? parseInt(this.product.purchaseStore) : null,
        currentStore: this.product.currentStore ? parseInt(this.product.currentStore) : null,
        purchaseDate: this.product.purchaseDate + "T00:00:00",
        salesDate: this.product.saleDate ? this.product.saleDate + "T00:00:00" : null,
        publicStateId: this.product.publicationStatus ? parseInt(this.product.publicationStatus) : null,
        shopMemoField: this.product.shopMemo,
        tagIds: this.product.selectedTags.map(id => parseInt(id)),
        imageDataList: imageDataList,
        isChanged: this.product.isChanged,
        deletedImageIds: this.product.deletedImageIds || [], // 削除された画像IDリスト
        selectedSalesChannelId: this.selectedSalesChannelId || 0,
      };
      
      // APIを呼び出して商品を登録
      const response = await service.registerProductInfo(productData);
      
      // 登録/更新成功時の処理
      let actionMessage = '';
      if (action === 'register') {
        actionMessage = '登録';
      } else if (action === 'update') {
        actionMessage = '更新';
      } else if (action === 'public') {
        actionMessage = '公開';
      }

      if (!response.data.success) {
        alert(`商品の${actionMessage}に失敗しました`);
        return;
      }

      alert(`商品が正常に${actionMessage}されました。`);

      if (action === 'update' || action === 'public') {
        // 遷移元に応じて画面遷移先を変更
        const sourceView = this.$route.query.source;
        if (sourceView === 'barcode') {
          // バーコード指定商品一覧から来た場合はバーコード指定商品一覧に戻る
          this.$router.push('/products/barcode');
        }
      } else {
        // 新規登録時はフォームをリセット
        this.resetForm();
      }
    },

    // 画像データの処理
    async processImageDataList(images) {
      const imageDataList = [];

      if (images.length === 0) return imageDataList;

      for (let i = 0; i < images.length; i++) {
        const image = images[i];
        const sortOrder = i; // 0から始まる順番

        // 既存画像
        if (image.isExisting && image.id) {
          imageDataList.push({
            id: image.id,
            data: null,
            extension: null,
            sortOrder: sortOrder
          });
        }
        // 新規アップロード（File）
        else if (image.file instanceof File) {
          const extension = '.' + image.file.name.split('.').pop().toLowerCase();
          const reader = new FileReader();

          reader.readAsDataURL(image.file);

          await new Promise(resolve => {
            reader.onload = () => {
              const base64data = reader.result.split(',')[1];
              imageDataList.push({
                id: null,
                data: base64data,
                extension: extension,
                sortOrder: sortOrder
              });
              resolve();
            };
          });
        }
        // Base64 データ
        else if (typeof image.preview === 'string' && image.preview.startsWith('data:image')) {
          const base64data = image.preview.split(',')[1];
          let extension = '.jpg'; // デフォルト
          if (image.preview.includes('image/png')) extension = '.png';
          else if (image.preview.includes('image/jpeg')) extension = '.jpg';
          else if (image.preview.includes('image/bmp')) extension = '.bmp';

          imageDataList.push({
            id: null,
            data: base64data,
            extension: extension,
            sortOrder: sortOrder
          });
        }
        // Blob URL（新規アップロード画像でfileプロパティが失われた場合）
        else if (typeof image.preview === 'string' && image.preview.startsWith('blob:') && image.fileName) {
          try {
            const response = await fetch(image.preview);
            const blob = await response.blob();
            const reader = new FileReader();
            
            await new Promise(resolve => {
              reader.onload = () => {
                const base64data = reader.result.split(',')[1];
                const extension = '.' + image.fileName.split('.').pop().toLowerCase();
                imageDataList.push({
                  id: null,
                  data: base64data,
                  extension: extension,
                  sortOrder: sortOrder
                });
                resolve();
              };
              reader.readAsDataURL(blob);
            });
          } catch (error) {
            console.error('Blob URLからの画像データ取得に失敗:', error);
          }
        }
      }

      return imageDataList;
    },

    /**
     * 商品説明テンプレートを更新する
     */
    async updateProductDescriptionTemplate() {
      const categoryId = this.product.category;
      const rankId = this.product.rank;
      if (categoryId && rankId) {
        showLoading();
        try {
          const response = await service.getProductDescriptionTemplate(parseInt(categoryId), parseInt(rankId));
          console.log('商品説明テンプレートの取得:', response);
          if (response && response.status === 200 && response.data) {
            // 商品説明欄に反映
            this.product.productDescription = response.data.templateStatement;
          }
        } catch (error) {
          console.error('商品説明テンプレートの取得に失敗しました:', error);
          alert('商品説明テンプレートの取得中にエラーが発生しました。');
        } finally {
          hideLoading();
        }
      }
    },

    // バリデーションチェック
    validationCheck() {
      // 必須項目のバリデーションチェック
      if (!this.product.productName) {
        return '商品名は必須です';
      }
      if (!this.product.rank) {
        return 'ランクは必須です';
      }
      if (!this.product.accessoryId) {
        return '付属品は必須です';
      }
      if (!this.product.purchasePrice) {
        return '買取価格は必須です';
      }
      if (!this.product.sellingPrice) {
        return '販売価格は必須です';
      }
      if (!this.product.purchaseStore) {
        return '買取店舗は必須です';
      }
      if (!this.product.currentStore) {
        return '現在店舗は必須です';
      }
      if (!this.product.purchaseDate) {
        return '買取日は必須です';
      }
      if (!this.product.publicationStatus) {
        return '公開状態は必須です';
      }
      if (!this.product.category) {
        return 'カテゴリは必須です';
      }
      
      // 権限チェック
      if (this.userAuthority === constants.Authority.MANAGER.id || this.userAuthority === constants.Authority.STAFF.id) {
        // 現在店舗が自店舗かチェック
        if (parseInt(this.product.currentStore) !== this.userStoreId) {
          return '自店舗の商品のみ登録・更新できます。現在店舗に他店舗が選択されています。';
        }
      }
      
      // 桁数チェック
      if (64 < this.product.productName.length) {
        return '商品名は64文字以内で入力してください';
      }
      if (1000 < this.product.productDescription.length) {
        return '商品説明は1000文字以内で入力してください';
      }
      if (1000 < this.product.shopMemo.length) {
        return 'ショップ用メモ欄は1000文字以内で入力してください';
      }
      return null;
    },
    
    // フォームをリセットする
    resetForm() {
      // ユーザーの店舗IDを取得
      const userStoreId = this.$store.getters['auth/currentUser']?.storeId;
      const userStoreIdStr = userStoreId ? userStoreId.toString() : '';
      
      // 価格調整をリセット
      this.priceAdjustment = '';
      if (this.$refs.priceAdjustmentField) {
        this.$emit('update:modelValue', '');
      }
      
      // FormSelectSearchFieldコンポーネントのv-modelを直接更新する
      this.product.makerName = '';
      
      this.product = {
        productCode: '',
        makerName: '',
        productName: '',
        rank: '',
        accessoryId: 0 < this.accessoryOptions.length ? this.accessoryOptions[0].value : null,
        productDescription: '',
        purchasePrice: null,
        sellingPrice: null,
        sellingPriceExTax: null,
        stockQuantity: 1,
        purchaseStore: userStoreIdStr, // ユーザーの店舗IDをデフォルト値として設定
        currentStore: userStoreIdStr, // ユーザーの店舗IDをデフォルト値として設定
        purchaseDate: this.getTodayDate(), // 本日日付をデフォルト値に設定
        saleDate: '',
        publicationStatus: '',
        labelSize: '', // ラベルのデフォルト選択を解除
        category: '',
        selectedTags: [],
        images: [],
        registrationDate: '',
        updateDate: '',
        lastUpdatedBy: '',
        shopMemo: ''
      };
      
      // カテゴリ選択状態をリセット
      const firstCategory = this.categories[0].items[0];
      this.product.category = firstCategory.value;
      // カテゴリ選択時の処理を呼び出してランクとラベルを設定
      this.handleCategorySelection(firstCategory.value);
      this.updateRankOptions('');
      
      // ランクオプションをリセット
      this.rankOptions = [];
      
      // 現在店舗の選択に基づいてタグを設定
      if (!userStoreId) return;
      this.handleStoreSelection(userStoreIdStr);

      // 公開ステータスのデフォルト設定
      this.setDefaultProductStatus();
    },
    // priceByChannelから金額を取得する
    getChannelPriceFromResponse(channelId) {
      const found = this.priceByChannel.find(p => p.salesChannelId === channelId);
      return found ? found.sellingPriceTaxIncluded : null;
    },
    
    // 販売チャンネルIDから販売チャンネル名を取得する
    getSalesChannelName(salesChannelId) {
      if (!salesChannelId) return '';
      const channel = this.salesChannelOptions.find(option => option.value === salesChannelId.toString());
      return channel ? channel.label : '';
    },
  }
}
</script>

<style scoped>
.product-register-form {
  display: flex;
  background-color: var(--gray-light);
  gap: 0;
}

.form-left {
  width: 80%;
  background-color: var(--gray-light);
  padding-right: 0;
}

.form-right {
  width: 20%;
  min-width: 200px;
  background-color: var(--gray-light);
  padding-left: 0;
}

.form-section {
  background-color: #fff;
  border-radius: 4px;
  padding: 1rem;
  margin: 0.05rem;
  margin-bottom: 1rem;
}

.form-row {
  margin-bottom: 1rem;
}

.form-group {
  margin-bottom: 0.5rem;
}

.required-badge {
  color: #dc3545;
  font-size: 0.8rem;
}

.sidebar-title {
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 1rem;
}

.sidebar-section {
  margin-bottom: 1.5rem;
}

.category-search {
  margin-bottom: 1rem;
}

.category-list {
  max-height: 300px;
  overflow-y: auto;
}

.status-list {
  list-style-type: none;
  padding: 0;
  margin: 0;
}

.status-item {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}

.status-label {
  margin-left: 8px;
}

.status-buttons {
  display: flex;
  flex-direction: column;
}

.price-inputs {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.footer-publication-status {
  width: 200px;
  margin-right: 1rem;
}

/* フッター固定に対応するためのパディング */
.content {
  padding-bottom: 80px !important;
}

.image-upload-area {
  border: 2px dashed #ccc;
  padding: 1.5rem;
  text-align: center;
  border-radius: 4px;
}

.image-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.sales-channel-item {
  margin-bottom: 5px;
}

.channel-price {
  font-weight: bold;
  color: #333;
}

.price-warning {
  margin-left: 10px;
  color: #dc3545;
  font-size: 0.85rem;
}

/* Yellowfish specific classes */
.A11 {
  font-weight: 700;
  min-width: 180px;
  flex: 0 0 180px;
}

.A11-long {
  font-weight: 700;
  min-width: 180px;
  flex: 0 0 180px;
}

.A13 {
  display: flex;
  align-items: center;
}

.mis45 {
  width: 100%;
}

.enclosure {
  display: flex;
  align-items: center;
  background-color: #FFF;
  padding: 10px 30px;
  margin: 5px;
  border-radius: 4px;
}

.enclosure input[type="text"],
.enclosure input[type="number"],
.enclosure input[type="date"],
.enclosure select,
.enclosure textarea {
  width: calc(100% - 230px);
  min-width: 200px;
  border: 2px solid #195983;
  border-radius: 4px;
  padding: 8px;
  box-sizing: border-box;
}

.d-flex {
  display: flex;
}

.column {
  flex-direction: column;
}

.A7-items {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.A7-item {
  display: flex;
  align-items: center;
}

.A7-label {
  margin-left: 0.25rem;
  margin-bottom: 0;
}

.A22 {
  font-weight: 600;
  font-size: 1rem;
}

.enclosure-sider {
  background-color: #fff;
  border-radius: 4px;
  padding: 1rem;
  margin: 0.05rem;
  margin-bottom: 1rem;
}

.footer-section {
  margin-top: 1rem;
  display: flex;
  justify-content: flex-end;
}

.footer-sales-channel {
  margin-top: 8px;
}

/* 価格変更履歴テーブルのスタイル */
.price-history-table {
  margin-top: 10px;
  overflow-x: auto;
}

.price-history-table table {
  width: 100%;
  border-collapse: collapse;
}

.price-history-table th {
  background-color: #f5f5f5;
  padding: 8px 12px;
  text-align: left;
  font-weight: bold;
  border-bottom: 2px solid #ddd;
}

.price-history-table td {
  padding: 8px 12px;
  border-bottom: 1px solid #eee;
}

.no-price-history {
  padding: 15px;
  text-align: center;
  color: #666;
  font-style: italic;
}
</style>
